import { useMemo, useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import TimeTrackingPage from "./TimeTrackingPage";
import { timeTrackingApi } from "../../../api/timeTrackingApi";
import { employeesApi } from "../../../api/employeesApi";
import { TimeTrackingEntry } from "../../../types/timeTracking";
import { EmployeeUserType } from "../../../types/employeeType";
import { TableCard } from "./TimeTracking.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import dayjs from "dayjs";

const TimeTrackingAllEntriesPage = () => {
  const { t } = useTranslation();
  const { serviceModuleId } = useParams<{ serviceModuleId: string }>();
  const parsedServiceModuleId = Number(serviceModuleId);
  const [pendingEntries, setPendingEntries] = useState<TimeTrackingEntry[]>([]);
  const [employees, setEmployees] = useState<EmployeeUserType[]>([]);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const loadEmployees = useCallback(async () => {
    const response = await employeesApi.getServiceModuleEmployees(parsedServiceModuleId);
    if (response.success() && response.result) {
      setEmployees(response.result);
    } else {
      setEmployees([]);
    }
  }, []);

  const loadPendingEntries = useCallback(async () => {
    if (!Number.isFinite(parsedServiceModuleId)) return;
    setIsLoadingPending(true);
    const result = await timeTrackingApi.getCompanyEntries(parsedServiceModuleId, {
      limit: 50,
      offset: 0,
      approval: "Pending",
    });
    if (result.success() && result.result) {
      setPendingEntries(result.result.items);
    } else {
      setPendingEntries([]);
    }
    setIsLoadingPending(false);
  }, [parsedServiceModuleId]);

  useEffect(() => {
    loadEmployees();
    loadPendingEntries();
  }, [loadEmployees, loadPendingEntries]);

  const getEmployeeName = useCallback(
    (employeeId: string) => {
      const employee = employees.find((item) => item.id === employeeId);
      if (!employee) return employeeId;
      return `${employee.firstName} ${employee.lastName}`.trim();
    },
    [employees]
  );

  const handleApproval = useCallback(
    async (entry: TimeTrackingEntry, approval: "Approved" | "Rejected") => {
      if (!Number.isFinite(parsedServiceModuleId)) return;
      setUpdatingId(entry.id);
      const result = await timeTrackingApi.updateTimeTrackingApproval(
        parsedServiceModuleId,
        entry.id,
        approval
      );
      if (result.success()) {
        loadPendingEntries();
      }
      setUpdatingId(null);
    },
    [loadPendingEntries, parsedServiceModuleId]
  );

  const formatDateTime = useCallback((value: string) => {
    return dayjs(value).format("DD.MM.YYYY HH:mm");
  }, []);
  const approvalButtonSx = useMemo(
    () => ({ minHeight: 30, padding: "0.35rem 0.75rem", fontSize: "0.8125rem" }),
    []
  );

  const breadcrumbs = useMemo(
    () => [
      {
        title: "serviceModulesPage.serviceModules",
        link: "serviceModules",
      },
      {
        title: t("serviceModulesPage.timetrackingPage.allEntriesCard.title"),
        link: `serviceModules/timetracking/${serviceModuleId}/all`,
      },
    ],
    [serviceModuleId, t]
  );

  return (
    <>
      <PageTitle
        breadcrumbs={breadcrumbs}
        title={t("serviceModulesPage.timetrackingPage.allEntriesCard.title")}
      />
      <Box sx={{ mt: 3 }}>
        <TableCard>
          <Typography variant="h6">
            {t("serviceModulesPage.timetrackingPage.approvals.title")}
          </Typography>
          {isLoadingPending ? (
            <CircularProgress />
          ) : pendingEntries.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {t("serviceModulesPage.timetrackingPage.approvals.empty")}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("serviceModulesPage.timetrackingPage.table.employee")}
                  </TableCell>
                  <TableCell>
                    {t("serviceModulesPage.timetrackingPage.table.start")}
                  </TableCell>
                  <TableCell>
                    {t("serviceModulesPage.timetrackingPage.table.end")}
                  </TableCell>
                  <TableCell align="right">
                    {t("serviceModulesPage.timetrackingPage.table.netWorkingHours")}
                  </TableCell>
                  <TableCell>
                    {t("serviceModulesPage.timetrackingPage.approvals.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{getEmployeeName(entry.employeeUserId)}</TableCell>
                    <TableCell>{formatDateTime(entry.startTimestamp)}</TableCell>
                    <TableCell>{formatDateTime(entry.endTimestamp)}</TableCell>
                    <TableCell align="right">{entry.netWorkingHours}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <GeneralButton
                          appVariant="primary"
                          onClick={() => handleApproval(entry, "Approved")}
                          loading={updatingId === entry.id}
                          sx={approvalButtonSx}
                        >
                          {t("serviceModulesPage.timetrackingPage.approvals.approve")}
                        </GeneralButton>
                        <GeneralButton
                          appVariant="secondary"
                          onClick={() => handleApproval(entry, "Rejected")}
                          loading={updatingId === entry.id}
                          sx={approvalButtonSx}
                        >
                          {t("serviceModulesPage.timetrackingPage.approvals.reject")}
                        </GeneralButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableCard>
        <Box sx={{ mt: 3 }}>
        <TimeTrackingPage
          serviceModuleId={Number(parsedServiceModuleId)}
          hidePageTitle
        />
        </Box>
      </Box>
    </>
  );
};

export default TimeTrackingAllEntriesPage;
